import Axios from 'axios';
import { getHeaders, getHeadersMultipart } from './headers';
import appConfig from '../../config/appConfig';
import { clearSession } from '../../utils/auth/session';

// Guards against firing the teardown more than once while several in-flight
// requests all reject with 401 at the same time (e.g. the bootstrap fan-out on
// return-to-site). Without this, each 401 would re-trigger clearSession and the
// reload could race.
let sessionTeardownStarted = false;

function handleUnauthorized() {
  // Fire once per page life. Several in-flight requests can reject with 401 at
  // the same time (the bootstrap fan-out on return-to-site); we only tear down
  // once. This is also what makes the stuck-user rescue self-terminating: the
  // first 401 clears localStorage and reloads '/', so the stale Cognito token
  // is gone and no further bootstrap request fires to 401 again.
  if (sessionTeardownStarted) return;
  sessionTeardownStarted = true;
  clearSession({ expired: true });
}

const configApi = {
  baseURL: `${appConfig.api.ENDPOINT}`,
  timeout: 15000,
};

const configApiMultipart = {
  baseURL: `${appConfig.api.ENDPOINT}`,
  timeout: 30000,
};

// Shared response-error handler for every axios instance. Normalizes the error
// shape callers depend on (err.normalizedError) and centralizes 401 teardown.
function onResponseError(error) {
  const normalizedError = {
    message: error?.response?.data?.message || error?.message || 'Unexpected API error',
    status: error?.response?.status || null,
    data: error?.response?.data || null,
  };

  // 401 means the token was missing/expired/invalid (backend
  // UnauthorizedException). This is the owning layer for session teardown:
  // clear the stale session and route to login so no caller has to. A 403 is a
  // permission failure for an otherwise-valid session and must NOT clear it, so
  // it is deliberately left to propagate to the caller.
  if (normalizedError.status === 401) {
    handleUnauthorized();
  }

  const enhancedError = { ...error, normalizedError };
  return Promise.reject(enhancedError);
}

class Api {
  axios = Axios.create(configApi);

  axiosMultipart = Axios.create(configApiMultipart);

  constructor() {
    this.axios.interceptors.response.use((response) => response, onResponseError);
    this.axiosMultipart.interceptors.response.use((response) => response, onResponseError);
  }

  async post(url, data, header) {
    const headers = header || getHeaders();
    return this.axios.post(url, data, { headers });
  }

  async put(url, data, header) {
    const headers = header || getHeaders();
    return this.axios.put(url, data, { headers });
  }

  async patch(url, data, header) {
    const headers = header || getHeaders();
    return this.axios.patch(url, data, { headers });
  }

  async get(url, params, header) {
    const headers = header || getHeaders();
    return this.axios.get(url, { params, headers });
  }

  async delete(url, params, header) {
    const headers = header || getHeaders();
    return this.axios.delete(url, { params, headers });
  }

  async postMultipart(url, data, headersMultipart) {
    const headers = headersMultipart || getHeadersMultipart();
    return this.axiosMultipart.post(url, data, { headers });
  }
}

export default new Api();
